import type { ApplicationType } from 'api-validations/stitch'

import type { AssetIdentity, SatelliteImagery, SoilSurveyAttribute } from '../data-layer-state'

/**
 * What a selection is anchored to. The map has exactly two identifier spaces:
 * as-applied data is keyed by stream, everything else by planning field.
 */
export type LayerScope =
  | { kind: 'Stream'; streamId: string }
  | { kind: 'PlanningField'; planningFieldId: string }

/**
 * A line of equivalency between layers in different scopes — not an identifier.
 * Two layers sharing a key are the same *kind* of thing, which is what makes
 * one a reasonable landing place when the user leaves the scope holding the
 * other. A single stream can carry several layers matching one key: a planter
 * with two liquid systems has a rate layer on each.
 *
 * The per-scope details a selection also carries — `assetId`,
 * `samplingEventId`, `captureDate`, `mergedStreamIds` — are deliberately
 * absent. Each names something that exists in one scope and has no counterpart
 * in another, so a scope change must look up a replacement rather than carry
 * the old value. That absence is why rebuilding a selection needs the catalog.
 *
 * The two editor variants have no key: an editing session names a specific plan
 * in a specific field, so a scope change ends it rather than relocating it. Nor
 * does a bare field, which is the scope itself with nothing chosen inside it.
 */
export type LayerEquivalencyKey =
  | AsAppliedEquivalencyKey
  | { kind: 'Satellite'; imagery: SatelliteImagery }
  | { kind: 'SoilSample'; measurement: string }
  | { kind: 'SoilSurvey'; attribute: SoilSurveyAttribute }
  | { kind: 'PlanAsset'; assetType: AssetIdentity['assetType'] }

/**
 * Fields are ordered by how much they must hold: `appTypeKey` and `layerKey`
 * name the kind of data and are required for any equivalency at all, while
 * `applicationName` distinguishes instances of that kind and is only a
 * preference — the operator may have named the same system differently on
 * another machine, or the target stream may carry only one instance.
 */
export type AsAppliedEquivalencyKey = {
  kind: 'AsApplied'
  appTypeKey: ApplicationType
  /** User-entered, so it separates two liquid systems the type key cannot. */
  applicationName: string
  layerKey: string
}

/**
 * How well the scope the user moved to could honor the key they were on.
 * Graded rather than boolean because the middle case is common and materially
 * different from the last: keeping the metric on a different system is close
 * to what the user asked for, while dropping to Coverage is not.
 */
export enum EquivalencyOutcome {
  /** Every field of the key matched. */
  Exact = 'Exact',
  /** Matched without `applicationName` — same metric, different instance. */
  Equivalent = 'Equivalent',
  /** Nothing matched; the scope's default layer stands in. */
  Substituted = 'Substituted',
  /** No matching required: the action named a concrete target or changed no scope. */
  NotApplicable = 'NotApplicable'
}
